import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, concatMap, switchMap, map, mergeMap, withLatestFrom } from 'rxjs/operators';
import { AppState } from '@get/store/configs/reducers';
import { StoreActionType } from '@enums';
import { getMultiAction } from '@get/store/configs/batched-actions';
import { Societe, SocieteEntityState } from '@get/api-interfaces';
import { SocieteApiService } from '@get/store/api-services';
import { SocieteGeneratedActions } from '@get/store/actions';
import { getActionsToNormalizeSociete } from '@get/store/configs/normalization';
import { SocieteSelectors } from '@get/store/selectors';
import { SocieteRelationsIds } from '@get/store/ids-interfaces';
import { OrganisationGeneratedActions } from '@get/store/actions';
import { SocieteComposantGeneratedActions } from '@get/store/actions';
import { SocieteComposant } from '@get/api-interfaces';
import { SocietePatrimoineHierarchieGeneratedActions } from '@get/store/actions';
import { SocietePatrimoineHierarchie } from '@get/api-interfaces';
import { SocieteCaracteristiqueGeneratedActions } from '@get/store/actions';
import { SocieteCaracteristique } from '@get/api-interfaces';
import { SocieteComposantFamilleGeneratedActions } from '@get/store/actions';
import { SocieteComposantFamille } from '@get/api-interfaces';
import { SocieteEspaceFamilleGeneratedActions } from '@get/store/actions';
import { SocieteEspaceFamille } from '@get/api-interfaces';
import { CampagneGeneratedActions } from '@get/store/actions';
import { Campagne } from '@get/api-interfaces';
import { SocieteTerritoireGeneratedActions } from '@get/store/actions';
import { SocieteTerritoire } from '@get/api-interfaces';
import { SocieteProfilGeneratedActions } from '@get/store/actions';
import { SocieteProfil } from '@get/api-interfaces';
import { PatrimoineGeneratedActions } from '@get/store/actions';
import { Patrimoine } from '@get/api-interfaces';
import { SocieteConfigAnalyseSyntheseGeneratedActions } from '@get/store/actions';
import { SocieteConfigAnalyseSynthese } from '@get/api-interfaces';

export function getDefaultAddSocieteActions(societe: SocieteEntityState, ids?: SocieteRelationsIds): Action[] {
  const actions: Action[] = [SocieteGeneratedActions.normalizeManySocietesAfterUpsert({ societes: [societe] })];

  if (ids?.organisation) {
    actions.push(
      OrganisationGeneratedActions.addManySocieteSuccess({
        idOrganisation: ids.organisation,
        idSocietes: [societe.idSociete]
      })
    );
    actions.push(
      SocieteGeneratedActions.addOrganisationSuccess({
        idSociete: societe.idSociete,
        idOrganisation: ids.organisation
      })
    );
  }

  if (ids?.societeComposants) {
    if (!Array.isArray(ids.societeComposants)) {
      actions.push(
        SocieteComposantGeneratedActions.upsertOneSocieteComposant({
          societeComposant: {
            idSociete: societe.idSociete,
            idSocieteComposant: ids.societeComposants as number
          } as SocieteComposant
        })
      );
      actions.push(
        SocieteGeneratedActions.addManySocieteComposantSuccess({
          idSociete: societe.idSociete,
          idSocieteComposants: [ids.societeComposants as number]
        })
      );
    } else {
      actions.push(
        SocieteComposantGeneratedActions.upsertManySocieteComposants({
          societeComposants: (ids.societeComposants as number[]).map(
            (idSocieteComposant: number) => ({
              idSociete: societe.idSociete,
              idSocieteComposant
            })
          ) as SocieteComposant[]
        })
      );
      actions.push(
        SocieteGeneratedActions.addManySocieteComposantSuccess({
          idSociete: societe.idSociete,
          idSocieteComposants: ids.societeComposants as number[]
        })
      );
    }
  }

  if (ids?.societePatrimoineHierarchies) {
    if (!Array.isArray(ids.societePatrimoineHierarchies)) {
      actions.push(
        SocietePatrimoineHierarchieGeneratedActions.upsertOneSocietePatrimoineHierarchie({
          societePatrimoineHierarchie: {
            idSociete: societe.idSociete,
            idSocietePatrimoineHierarchie: ids.societePatrimoineHierarchies as number
          } as SocietePatrimoineHierarchie
        })
      );
      actions.push(
        SocieteGeneratedActions.addManySocietePatrimoineHierarchieSuccess({
          idSociete: societe.idSociete,
          idSocietePatrimoineHierarchies: [ids.societePatrimoineHierarchies as number]
        })
      );
    } else {
      actions.push(
        SocietePatrimoineHierarchieGeneratedActions.upsertManySocietePatrimoineHierarchies({
          societePatrimoineHierarchies: (ids.societePatrimoineHierarchies as number[]).map(
            (idSocietePatrimoineHierarchie: number) => ({
              idSociete: societe.idSociete,
              idSocietePatrimoineHierarchie
            })
          ) as SocietePatrimoineHierarchie[]
        })
      );
      actions.push(
        SocieteGeneratedActions.addManySocietePatrimoineHierarchieSuccess({
          idSociete: societe.idSociete,
          idSocietePatrimoineHierarchies: ids.societePatrimoineHierarchies as number[]
        })
      );
    }
  }

  if (ids?.societeCaracteristiques) {
    if (!Array.isArray(ids.societeCaracteristiques)) {
      actions.push(
        SocieteCaracteristiqueGeneratedActions.upsertOneSocieteCaracteristique({
          societeCaracteristique: {
            idSociete: societe.idSociete,
            idSocieteCaracteristique: ids.societeCaracteristiques as number
          } as SocieteCaracteristique
        })
      );
      actions.push(
        SocieteGeneratedActions.addManySocieteCaracteristiqueSuccess({
          idSociete: societe.idSociete,
          idSocieteCaracteristiques: [ids.societeCaracteristiques as number]
        })
      );
    } else {
      actions.push(
        SocieteCaracteristiqueGeneratedActions.upsertManySocieteCaracteristiques({
          societeCaracteristiques: (ids.societeCaracteristiques as number[]).map(
            (idSocieteCaracteristique: number) => ({
              idSociete: societe.idSociete,
              idSocieteCaracteristique
            })
          ) as SocieteCaracteristique[]
        })
      );
      actions.push(
        SocieteGeneratedActions.addManySocieteCaracteristiqueSuccess({
          idSociete: societe.idSociete,
          idSocieteCaracteristiques: ids.societeCaracteristiques as number[]
        })
      );
    }
  }

  if (ids?.societeComposantFamilles) {
    if (!Array.isArray(ids.societeComposantFamilles)) {
      actions.push(
        SocieteComposantFamilleGeneratedActions.upsertOneSocieteComposantFamille({
          societeComposantFamille: {
            idSociete: societe.idSociete,
            idSocieteComposantFamille: ids.societeComposantFamilles as number
          } as SocieteComposantFamille
        })
      );
      actions.push(
        SocieteGeneratedActions.addManySocieteComposantFamilleSuccess({
          idSociete: societe.idSociete,
          idSocieteComposantFamilles: [ids.societeComposantFamilles as number]
        })
      );
    } else {
      actions.push(
        SocieteComposantFamilleGeneratedActions.upsertManySocieteComposantFamilles({
          societeComposantFamilles: (ids.societeComposantFamilles as number[]).map(
            (idSocieteComposantFamille: number) => ({
              idSociete: societe.idSociete,
              idSocieteComposantFamille
            })
          ) as SocieteComposantFamille[]
        })
      );
      actions.push(
        SocieteGeneratedActions.addManySocieteComposantFamilleSuccess({
          idSociete: societe.idSociete,
          idSocieteComposantFamilles: ids.societeComposantFamilles as number[]
        })
      );
    }
  }

  if (ids?.societeEspaceFamilles) {
    if (!Array.isArray(ids.societeEspaceFamilles)) {
      actions.push(
        SocieteEspaceFamilleGeneratedActions.upsertOneSocieteEspaceFamille({
          societeEspaceFamille: {
            idSociete: societe.idSociete,
            idSocieteEspaceFamille: ids.societeEspaceFamilles as number
          } as SocieteEspaceFamille
        })
      );
      actions.push(
        SocieteGeneratedActions.addManySocieteEspaceFamilleSuccess({
          idSociete: societe.idSociete,
          idSocieteEspaceFamilles: [ids.societeEspaceFamilles as number]
        })
      );
    } else {
      actions.push(
        SocieteEspaceFamilleGeneratedActions.upsertManySocieteEspaceFamilles({
          societeEspaceFamilles: (ids.societeEspaceFamilles as number[]).map(
            (idSocieteEspaceFamille: number) => ({
              idSociete: societe.idSociete,
              idSocieteEspaceFamille
            })
          ) as SocieteEspaceFamille[]
        })
      );
      actions.push(
        SocieteGeneratedActions.addManySocieteEspaceFamilleSuccess({
          idSociete: societe.idSociete,
          idSocieteEspaceFamilles: ids.societeEspaceFamilles as number[]
        })
      );
    }
  }

  if (ids?.campagnes) {
    if (!Array.isArray(ids.campagnes)) {
      actions.push(
        CampagneGeneratedActions.upsertOneCampagne({
          campagne: {
            idSociete: societe.idSociete,
            idCampagne: ids.campagnes as number
          } as Campagne
        })
      );
      actions.push(
        SocieteGeneratedActions.addManyCampagneSuccess({
          idSociete: societe.idSociete,
          idCampagnes: [ids.campagnes as number]
        })
      );
    } else {
      actions.push(
        CampagneGeneratedActions.upsertManyCampagnes({
          campagnes: (ids.campagnes as number[]).map(
            (idCampagne: number) => ({
              idSociete: societe.idSociete,
              idCampagne
            })
          ) as Campagne[]
        })
      );
      actions.push(
        SocieteGeneratedActions.addManyCampagneSuccess({
          idSociete: societe.idSociete,
          idCampagnes: ids.campagnes as number[]
        })
      );
    }
  }

  if (ids?.societeTerritoires) {
    if (!Array.isArray(ids.societeTerritoires)) {
      actions.push(
        SocieteTerritoireGeneratedActions.upsertOneSocieteTerritoire({
          societeTerritoire: {
            idSociete: societe.idSociete,
            idSocieteTerritoire: ids.societeTerritoires as number
          } as SocieteTerritoire
        })
      );
      actions.push(
        SocieteGeneratedActions.addManySocieteTerritoireSuccess({
          idSociete: societe.idSociete,
          idSocieteTerritoires: [ids.societeTerritoires as number]
        })
      );
    } else {
      actions.push(
        SocieteTerritoireGeneratedActions.upsertManySocieteTerritoires({
          societeTerritoires: (ids.societeTerritoires as number[]).map(
            (idSocieteTerritoire: number) => ({
              idSociete: societe.idSociete,
              idSocieteTerritoire
            })
          ) as SocieteTerritoire[]
        })
      );
      actions.push(
        SocieteGeneratedActions.addManySocieteTerritoireSuccess({
          idSociete: societe.idSociete,
          idSocieteTerritoires: ids.societeTerritoires as number[]
        })
      );
    }
  }

  if (ids?.societeProfils) {
    if (!Array.isArray(ids.societeProfils)) {
      actions.push(
        SocieteProfilGeneratedActions.upsertOneSocieteProfil({
          societeProfil: {
            idSociete: societe.idSociete,
            idSocieteProfil: ids.societeProfils as number
          } as SocieteProfil
        })
      );
      actions.push(
        SocieteGeneratedActions.addManySocieteProfilSuccess({
          idSociete: societe.idSociete,
          idSocieteProfils: [ids.societeProfils as number]
        })
      );
    } else {
      actions.push(
        SocieteProfilGeneratedActions.upsertManySocieteProfils({
          societeProfils: (ids.societeProfils as number[]).map(
            (idSocieteProfil: number) => ({
              idSociete: societe.idSociete,
              idSocieteProfil
            })
          ) as SocieteProfil[]
        })
      );
      actions.push(
        SocieteGeneratedActions.addManySocieteProfilSuccess({
          idSociete: societe.idSociete,
          idSocieteProfils: ids.societeProfils as number[]
        })
      );
    }
  }

  if (ids?.patrimoines) {
    if (!Array.isArray(ids.patrimoines)) {
      actions.push(
        PatrimoineGeneratedActions.upsertOnePatrimoine({
          patrimoine: {
            idSociete: societe.idSociete,
            idPatrimoine: ids.patrimoines as number
          } as Patrimoine
        })
      );
      actions.push(
        SocieteGeneratedActions.addManyPatrimoineSuccess({
          idSociete: societe.idSociete,
          idPatrimoines: [ids.patrimoines as number]
        })
      );
    } else {
      actions.push(
        PatrimoineGeneratedActions.upsertManyPatrimoines({
          patrimoines: (ids.patrimoines as number[]).map(
            (idPatrimoine: number) => ({
              idSociete: societe.idSociete,
              idPatrimoine
            })
          ) as Patrimoine[]
        })
      );
      actions.push(
        SocieteGeneratedActions.addManyPatrimoineSuccess({
          idSociete: societe.idSociete,
          idPatrimoines: ids.patrimoines as number[]
        })
      );
    }
  }

  if (ids?.societeConfigAnalyseSyntheses) {
    if (!Array.isArray(ids.societeConfigAnalyseSyntheses)) {
      actions.push(
        SocieteConfigAnalyseSyntheseGeneratedActions.upsertOneSocieteConfigAnalyseSynthese({
          societeConfigAnalyseSynthese: {
            idSociete: societe.idSociete,
            idSocieteConfigAnalyseSynthese: ids.societeConfigAnalyseSyntheses as number
          } as SocieteConfigAnalyseSynthese
        })
      );
      actions.push(
        SocieteGeneratedActions.addManySocieteConfigAnalyseSyntheseSuccess({
          idSociete: societe.idSociete,
          idSocieteConfigAnalyseSyntheses: [ids.societeConfigAnalyseSyntheses as number]
        })
      );
    } else {
      actions.push(
        SocieteConfigAnalyseSyntheseGeneratedActions.upsertManySocieteConfigAnalyseSyntheses({
          societeConfigAnalyseSyntheses: (ids.societeConfigAnalyseSyntheses as number[]).map(
            (idSocieteConfigAnalyseSynthese: number) => ({
              idSociete: societe.idSociete,
              idSocieteConfigAnalyseSynthese
            })
          ) as SocieteConfigAnalyseSynthese[]
        })
      );
      actions.push(
        SocieteGeneratedActions.addManySocieteConfigAnalyseSyntheseSuccess({
          idSociete: societe.idSociete,
          idSocieteConfigAnalyseSyntheses: ids.societeConfigAnalyseSyntheses as number[]
        })
      );
    }
  }

  return actions;
}

export function getDefaultDeleteSocieteActions(societe: SocieteEntityState): Action[] {
  const actions: Action[] = [SocieteGeneratedActions.deleteOneSocieteSuccess({ idSociete: societe.idSociete })];

  if (societe.organisation) {
    actions.push(
      OrganisationGeneratedActions.deleteManySocieteSuccess({
        idSocietes: [societe.idSociete],
        idOrganisations: [societe.organisation as number]
      })
    );
  }

  if (societe.societeComposants) {
    actions.push(
      SocieteComposantGeneratedActions.deleteManySocieteSuccess({
        idSocietes: [societe.idSociete],
        idSocieteComposants: societe.societeComposants as number[]
      })
    );
  }

  if (societe.societePatrimoineHierarchies) {
    actions.push(
      SocietePatrimoineHierarchieGeneratedActions.deleteManySocieteSuccess({
        idSocietes: [societe.idSociete],
        idSocietePatrimoineHierarchies: societe.societePatrimoineHierarchies as number[]
      })
    );
  }

  if (societe.societeCaracteristiques) {
    actions.push(
      SocieteCaracteristiqueGeneratedActions.deleteManySocieteSuccess({
        idSocietes: [societe.idSociete],
        idSocieteCaracteristiques: societe.societeCaracteristiques as number[]
      })
    );
  }

  if (societe.societeComposantFamilles) {
    actions.push(
      SocieteComposantFamilleGeneratedActions.deleteManySocieteSuccess({
        idSocietes: [societe.idSociete],
        idSocieteComposantFamilles: societe.societeComposantFamilles as number[]
      })
    );
  }

  if (societe.societeEspaceFamilles) {
    actions.push(
      SocieteEspaceFamilleGeneratedActions.deleteManySocieteSuccess({
        idSocietes: [societe.idSociete],
        idSocieteEspaceFamilles: societe.societeEspaceFamilles as number[]
      })
    );
  }

  if (societe.campagnes) {
    actions.push(
      CampagneGeneratedActions.deleteManySocieteSuccess({
        idSocietes: [societe.idSociete],
        idCampagnes: societe.campagnes as number[]
      })
    );
  }

  if (societe.societeTerritoires) {
    actions.push(
      SocieteTerritoireGeneratedActions.deleteManySocieteSuccess({
        idSocietes: [societe.idSociete],
        idSocieteTerritoires: societe.societeTerritoires as number[]
      })
    );
  }

  if (societe.societeProfils) {
    actions.push(
      SocieteProfilGeneratedActions.deleteManySocieteSuccess({
        idSocietes: [societe.idSociete],
        idSocieteProfils: societe.societeProfils as number[]
      })
    );
  }

  if (societe.patrimoines) {
    actions.push(
      PatrimoineGeneratedActions.deleteManySocieteSuccess({
        idSocietes: [societe.idSociete],
        idPatrimoines: societe.patrimoines as number[]
      })
    );
  }

  if (societe.societeConfigAnalyseSyntheses) {
    actions.push(
      SocieteConfigAnalyseSyntheseGeneratedActions.deleteManySocieteSuccess({
        idSocietes: [societe.idSociete],
        idSocieteConfigAnalyseSyntheses: societe.societeConfigAnalyseSyntheses as number[]
      })
    );
  }

  return actions;
}

export class GeneratedSocieteEffects {
  constructor(
    protected actions$: Actions,
    protected societeApiService: SocieteApiService,
    protected store$: Store<AppState>
  ) {}

  getManySocietes$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SocieteGeneratedActions.getManySocietes),
      switchMap(({ params }) =>
        this.societeApiService.getSocietes(params).pipe(
          map((societes: Societe[]) => {
            return SocieteGeneratedActions.normalizeManySocietesAfterUpsert({ societes });
          }),
          catchError(error => of(SocieteGeneratedActions.societesFailure({ error })))
        )
      )
    );
  });

  getOneSociete$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SocieteGeneratedActions.getOneSociete),
      switchMap(idSociete =>
        this.societeApiService.getSociete(idSociete).pipe(
          map((societe: Societe) => {
            return SocieteGeneratedActions.normalizeManySocietesAfterUpsert({ societes: [societe] });
          }),
          catchError(error => of(SocieteGeneratedActions.societesFailure({ error })))
        )
      )
    );
  });

  upsertOneSociete$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SocieteGeneratedActions.upsertOneSociete),
      concatMap(
        ({
          societe,
          ids
        }: {
          societe: Partial<Societe>;
          ids?: SocieteRelationsIds;
        }) => {
          if (societe.idSociete) {
            return this.societeApiService.updateSociete(societe).pipe(
              map((societeReturned: Societe) => {
                return SocieteGeneratedActions.normalizeManySocietesAfterUpsert({ societes: [societeReturned] });
              }),
              catchError(error => of(SocieteGeneratedActions.societesFailure({ error })))
            );
          } else {
            return this.societeApiService.addSociete(societe).pipe(
              mergeMap((societeReturned: Societe) => getDefaultAddSocieteActions(societeReturned, ids)),
              catchError(error => of(SocieteGeneratedActions.societesFailure({ error })))
            );
          }
        }
      )
    );
  });

  deleteOneSociete$ = createEffect(() => {
    const selectSocieteState$ = this.store$.select(SocieteSelectors.selectSocieteState);
    return this.actions$.pipe(
      ofType(SocieteGeneratedActions.deleteOneSociete),
      withLatestFrom(selectSocieteState$),
      concatMap(([{ idSociete }, state]) =>
        this.societeApiService.deleteSociete(idSociete).pipe(
          mergeMap(_success => getDefaultDeleteSocieteActions(state.entities[idSociete] as SocieteEntityState)),
          catchError(error => of(SocieteGeneratedActions.societesFailure({ error })))
        )
      )
    );
  });

  normalizeManySocietesAfterUpsert$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SocieteGeneratedActions.normalizeManySocietesAfterUpsert),
      concatMap(({ societes }) => {
        const actions: Action[] = getActionsToNormalizeSociete(societes, StoreActionType.upsert);
        return [getMultiAction(actions, '[Societe] Normalization After Upsert Success')];
      })
    );
  });
}
